import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Minimize2, Maximize2, Plus, Sun, Moon, Coffee, Sunset, BookOpen, Zap, Clock, TrendingUp, CornerDownLeft } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import { dispatchRateLimitError } from '../utils/api';
import { RateLimitError } from '../types/tier';

// Contextual Welcome Component - Dynamic prompts based on time and context
const ContextualWelcome: React.FC<{ userName?: string }> = ({ userName }) => {
  const location = useLocation();
  const [greeting, setGreeting] = useState({ title: '', subtitle: '', icon: Sun, suggestions: [] as string[] });

  useEffect(() => {
    const hour = new Date().getHours();
    const dayOfWeek = new Date().getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const path = location.pathname;
    
    // Time-based greeting
    let timeGreeting = '';
    let TimeIcon = Sun;
    
    if (hour >= 5 && hour < 12) {
      timeGreeting = 'Good morning';
      TimeIcon = Coffee;
    } else if (hour >= 12 && hour < 17) {
      timeGreeting = 'Good afternoon';
      TimeIcon = Sun;
    } else if (hour >= 17 && hour < 21) {
      timeGreeting = 'Good evening';
      TimeIcon = Sunset;
    } else {
      timeGreeting = 'Working late';
      TimeIcon = Moon;
    }

    // Context-aware suggestions based on page and time
    let contextSuggestions: string[] = [];
    let subtitle = '';

    if (path === '/library') {
      if (hour >= 5 && hour < 12) {
        subtitle = 'Start your day with a quick read';
        contextSuggestions = [
          'What topics do my saved articles cover?',
          'Give me key insights from my library',
          'Recommend something new to explore'
        ];
      } else if (hour >= 12 && hour < 17) {
        subtitle = 'Perfect time for deep learning';
        contextSuggestions = [
          'What do my articles say about...',
          'Find patterns in my saved content',
          'Search the web for related topics'
        ];
      } else if (hour >= 17 && hour < 21) {
        subtitle = 'Wind down with a review';
        contextSuggestions = [
          'Summarize my recent saves',
          'What themes appear in my library?',
          'Suggest new topics to explore'
        ];
      } else {
        subtitle = 'Night owl learning mode';
        contextSuggestions = [
          'Quick recap of my saved content',
          'What topics am I exploring?',
          'Recommend something interesting'
        ];
      }
    } else if (path === '/learning-paths') {
      subtitle = isWeekend ? 'Weekend learning time!' : 'Continue your journey';
      contextSuggestions = [
        'What trails do I have?',
        'Suggest a new topic to explore',
        'Show my progress overview'
      ];
    } else if (path.startsWith('/learning-paths/')) {
      subtitle = 'Focused learning session';
      contextSuggestions = [
        "What's next in this path?",
        'Explain this concept deeper',
        'Quiz me on this topic'
      ];
    } else if (path === '/spaces') {
      subtitle = 'Organize your knowledge';
      contextSuggestions = [
        'How are my spaces organized?',
        'Find related content across spaces',
        'Suggest space improvements'
      ];
    } else {
      subtitle = 'Your learning companion';
      contextSuggestions = [
        'What should I learn today?',
        'Show my learning stats',
        'Recommend something new'
      ];
    }

    setGreeting({
      title: `${timeGreeting}, ${userName || 'there'}`,
      subtitle,
      icon: TimeIcon,
      suggestions: contextSuggestions
    });
  }, [location.pathname, userName]);

  const Icon = greeting.icon;

  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-6">
      {/* Time-based icon - no circle background */}
      <div className="mb-4">
        <Icon className="w-8 h-8 text-blue-500 dark:text-blue-400" />
      </div>
      
      {/* Greeting */}
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
        {greeting.title}
      </h4>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        {greeting.subtitle}
      </p>

      {/* Contextual suggestions */}
      <div className="w-full space-y-2">
        <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
          Try asking
        </p>
        {greeting.suggestions.map((suggestion, idx) => (
          <button
            key={idx}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 
                       bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50
                       rounded-lg border border-gray-200 dark:border-gray-700
                       transition-colors group"
            onClick={() => {
              // Set the message in the input
              const event = new CustomEvent('chat-suggestion', { detail: suggestion });
              window.dispatchEvent(event);
            }}
          >
            <span className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              {suggestion}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ChatWidget: React.FC = () => {
  const { isOpen, activeConversationId, conversations, currentMessage, isLoading, unreadCount,
    openChat, closeChat, toggleChat, setActiveConversation, setCurrentMessage,
    addMessage, setLoading, setConversations, resetUnread
  } = useChatStore();

  const { token, user } = useAuthStore();
  const location = useLocation();
  const params = useParams<{ pathId?: string }>();
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [thinkingPhase, setThinkingPhase] = useState<'searching' | 'reading' | 'thinking' | null>(null);
  const [customTopic, setCustomTopic] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const floatingButtonRef = useRef<HTMLButtonElement>(null);

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  
  console.log('🔄 ChatWidget RENDER:', {
    conversationsCount: conversations.length,
    activeConversationId,
    activeConvExists: !!activeConversation,
    messagesCount: activeConversation?.messages?.length || 0
  });

  // Determine if chat should be visible on current page
  const shouldShowChat = () => {
    const path = location.pathname;
    return path === '/library' || path === '/spaces' || path === '/learning-paths' || path.startsWith('/learning-paths/');
  };

  // Removed loadConversations useEffect - handleOpen creates conversations when needed

  // Reset chat ONLY when navigating between different pages, not when clicking outside on same page
  const prevPathRef = useRef<string | null>(null);
  const isInitialMount = useRef(true);
  const resetInProgress = useRef(false);
  
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Skip on initial mount - just set the ref
    if (isInitialMount.current) {
      console.log('🚀 ChatWidget initial mount on:', currentPath);
      prevPathRef.current = currentPath;
      isInitialMount.current = false;
      return;
    }
    
    // Prevent multiple resets in quick succession
    if (resetInProgress.current) {
      console.log('⚠️ Reset already in progress, skipping...');
      return;
    }
    
    // If ref hasn't been set yet, set it and return (shouldn't happen but safety check)
    if (prevPathRef.current === null) {
      console.log('⚠️ prevPathRef was null, setting to:', currentPath);
      prevPathRef.current = currentPath;
      return;
    }
    
    const prevPath = prevPathRef.current;
    
    // Get base paths (without query params or hash) to compare actual page routes
    const prevPage = prevPath.split('?')[0].split('#')[0];
    const currentPage = currentPath.split('?')[0].split('#')[0];
    
    // Only reset if navigating to a DIFFERENT page route (not same page with different query/hash)
    if (prevPage !== currentPage && prevPath !== currentPath) {
      resetInProgress.current = true;
      console.log('🔄 Page navigation detected: resetting chat. From:', prevPage, 'To:', currentPage);
      useChatStore.getState().reset();
      prevPathRef.current = currentPath;
      // Reset flag after a short delay
      setTimeout(() => {
        resetInProgress.current = false;
      }, 500);
    } else {
      // Same page - do NOT reset, just update ref
      console.log('📍 Same page detected, NOT resetting chat. prevPage:', prevPage, 'currentPage:', currentPage);
      prevPathRef.current = currentPath;
    }
  }, [location.pathname]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && !isMaximized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized, isMaximized]);

  // Listen for chat suggestion clicks from ContextualWelcome
  useEffect(() => {
    const handleSuggestion = async (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      const suggestionText = customEvent.detail;
      
      // Ensure we have an active conversation before populating input
      if (!activeConversationId) {
        console.log('📝 No active conversation for suggestion, creating one first...');
        await createNewConversation();
        // Wait for conversation to be created
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setCurrentMessage(suggestionText);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    
    window.addEventListener('chat-suggestion', handleSuggestion);
    return () => window.removeEventListener('chat-suggestion', handleSuggestion);
  }, [setCurrentMessage, activeConversationId]);

  // Handle topic selection from inline input
  const handleTopicSelect = async (topic: string) => {
    setCustomTopic(''); // Clear input
    const query = `What do my saved articles cover about ${topic}?`;
    
    // If no active conversation, create one and send message in one go
    if (!activeConversationId) {
      await createNewConversation(query);
      return;
    }
    
    // Send directly to existing conversation
    setLoading(true);
    await sendMessageToConversation(activeConversationId, query);
  };

  // Click outside handled by backdrop now - removed useEffect

  const loadConversations = async () => {
    try {
      const response = await fetch('/api/chat/conversations?limit=10', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.data || []);
        
        // Set first conversation as active if none selected
        if (!activeConversationId && data.data && data.data.length > 0) {
          setActiveConversation(data.data[0].id);
          await loadConversationMessages(data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const updatedConversations = conversations.map(c =>
          c.id === conversationId ? { ...c, messages: data.data.messages } : c
        );
        setConversations(updatedConversations);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const createNewConversation = async (messageToSendAfterCreation?: string) => {
    try {
      console.log('🆕 Creating new conversation...', messageToSendAfterCreation ? `with pending message: ${messageToSendAfterCreation.substring(0, 20)}...` : '');
      setLoading(true);
      
      // Determine context based on current page
      let contextType = 'general';
      let contextId: string | undefined = undefined;
      let title = 'New Conversation';
      const path = location.pathname;
      
      if (path === '/library') {
        // Use 'general' for DB storage, actual context passed via additionalContext
        contextType = 'general';
        title = 'Library Chat';
      } else if (path === '/spaces') {
        // Use 'general' for DB storage, actual context passed via additionalContext
        contextType = 'general';
        title = 'Spaces Chat';
      } else if (params.pathId && path.startsWith('/learning-paths/')) {
        // On a specific learning path detail page
        contextType = 'learning_path';
        contextId = params.pathId;
        title = 'Trail Chat';
        console.log('📚 Creating conversation for learning path:', params.pathId);
      } else if (path === '/learning-paths') {
        // On learning paths list page - use 'general' context (no specific path referenced)
        contextType = 'general';
        title = 'Knowledge Hub Chat';
      }
      
      console.log(`📍 Context: ${contextType} (${title})`, contextId ? `ID: ${contextId}` : '');
      
      const response = await fetch('/api/chat/conversations', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contextType: contextType,
          contextId: contextId,
          title: title
        })
      });

      if (response.ok) {
        const data = await response.json();
        const newConv = { ...data.data, messages: [] };
        console.log('✅ Conversation created:', newConv.id, 'context:', contextType, contextId);
        
        // Update store with new conversation
        setConversations([newConv, ...conversations]);
        setActiveConversation(newConv.id);
        
        // If we have a message to send, send it immediately using the new conversation ID
        if (messageToSendAfterCreation) {
          console.log('📤 Sending queued message immediately after conversation creation...');
          // Don't set loading false yet - we're about to send a message
          await sendMessageToConversation(newConv.id, messageToSendAfterCreation);
        } else {
          setLoading(false);
        }
      } else {
        console.error('❌ Failed to create conversation:', response.status, response.statusText);
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ Failed to create conversation:', error);
      setLoading(false);
    }
  };

  // Helper function to send message to a specific conversation ID
  const sendMessageToConversation = async (conversationId: string, message: string) => {
    try {
      console.log('📨 sendMessageToConversation:', conversationId, message.substring(0, 20));
      
      // Start thinking animation
      const isLibraryContext = location.pathname === '/library';
      const isKnowledgeHubContext = location.pathname === '/learning-paths';
      if (isLibraryContext || isKnowledgeHubContext) {
        setThinkingPhase('searching');
        setTimeout(() => setThinkingPhase('reading'), 800);
        setTimeout(() => setThinkingPhase('thinking'), 1600);
      }

      // Add user message optimistically
      const userMessage = {
        id: Date.now().toString(),
        role: 'user' as const,
        content: message,
        created_at: new Date().toISOString()
      };
      addMessage(conversationId, userMessage);

      // Build context
      const additionalContext: any = {};
      const currentPath = location.pathname;
      
      if (params.pathId) {
        const storedIndex = sessionStorage.getItem(`learning-path-${params.pathId}-current-index`);
        const currentIndex = storedIndex !== null ? parseInt(storedIndex, 10) : 0;
        additionalContext.learningPathId = params.pathId;
        additionalContext.currentItemIndex = currentIndex;
        additionalContext.contextType = 'learning_path';
      } else if (currentPath === '/learning-paths') {
        additionalContext.contextType = 'learning_paths';
      } else if (currentPath === '/library') {
        additionalContext.contextType = 'library';
      } else if (currentPath === '/spaces') {
        additionalContext.contextType = 'spaces';
      }

      const response = await fetch(`/api/chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message, context: additionalContext })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Got bot response:', data.data.content.substring(0, 50) + '...');
        const assistantMessage = {
          id: data.data.messageId,
          role: 'assistant' as const,
          content: data.data.content,
          metadata: data.data.actions || {},
          created_at: new Date().toISOString()
        };
        addMessage(conversationId, assistantMessage);
      } else if (response.status === 429) {
        // Rate limit exceeded - dispatch event for upgrade modal
        const errorData = await response.json() as RateLimitError;
        dispatchRateLimitError(errorData);
        const errorMessage = {
          id: Date.now().toString(),
          role: 'assistant' as const,
          content: `You've reached your chat message limit. ${errorData.usage?.resetIn ? `Resets ${errorData.usage.resetIn}.` : ''} Upgrade your plan for more messages.`,
          created_at: new Date().toISOString()
        };
        addMessage(conversationId, errorMessage);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('❌ sendMessageToConversation error:', error);
      const errorMessage = {
        id: Date.now().toString(),
        role: 'assistant' as const,
        content: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString()
      };
      addMessage(conversationId, errorMessage);
    } finally {
      setLoading(false);
      setThinkingPhase(null);
    }
  };

  // Helper to send a message directly (used by quick actions)
  const sendMessageDirect = async (messageText: string) => {
    const message = messageText.trim();
    if (!message || isLoading) {
      console.log('⚠️ Message empty or loading, returning');
      return;
    }

    // If no active conversation, create one and send message in one go
    if (!activeConversationId) {
      console.log('📝 No active conversation, creating one and sending message...');
      await createNewConversation(message);
      return;
    }

    // Existing conversation - send directly
    console.log('✅ Sending message directly to conversation:', activeConversationId);
    setLoading(true);
    await sendMessageToConversation(activeConversationId, message);
  };

  const sendMessage = async () => {
    console.log('💬 sendMessage called, message:', currentMessage?.substring(0, 20), 'isLoading:', isLoading, 'activeConversationId:', activeConversationId);
    
    if (!currentMessage.trim() || isLoading) {
      console.log('⚠️ Message empty or loading, returning');
      return;
    }

    const message = currentMessage.trim();
    setCurrentMessage(''); // Clear input immediately

    // If no active conversation, create one and send message in one go
    if (!activeConversationId) {
      console.log('📝 No active conversation, creating one and sending message...');
      await createNewConversation(message);
      return;
    }

    // Existing conversation - send directly
    console.log('✅ Sending message to existing conversation:', activeConversationId);
    setLoading(true);
    await sendMessageToConversation(activeConversationId, message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Type for quick action buttons
  type QuickAction = {
    label: string;
    query: string;
    isInlineInput?: boolean;
    isHelpAction?: boolean;
  };

  // Get page-aware quick action buttons
  const getQuickActions = (): QuickAction[] => {
    const path = location.pathname;
    
    // Check if we're on a learning path detail page (has pathId param)
    const isDetailPage = params.pathId && path.startsWith('/learning-paths/') && path !== '/learning-paths';
    
    if (path === '/library') {
      return [
        { label: 'Ask My Library', query: 'ASK_LIBRARY_INLINE', isInlineInput: true },
        { label: 'Key Insights', query: 'Give me key insights and patterns from my saved content' },
        { label: 'Recommendations', query: 'Based on my reading interests, what topics should I explore next?' }
      ];
    } else if (isDetailPage) {
      // Learning Path Detail Page - Context-aware actions for current item
      return [
        { label: 'Explain Simply', query: 'EXPLAIN_SIMPLE', isHelpAction: true },
        { label: 'Quiz Me', query: 'QUIZ', isHelpAction: true },
        { label: 'Key Concepts', query: 'KEY_CONCEPTS', isHelpAction: true },
        { label: "What's Next?", query: 'WHATS_NEXT', isHelpAction: false }
      ];
    } else if (path === '/learning-paths' || path.startsWith('/learning-paths/')) {
      // Learning Paths List Page (Knowledge Hub) - persistent quick actions
      return [
        { label: 'My Trails', query: 'What trails do I have?' },
        { label: 'Suggest Topic', query: 'Suggest a new topic to explore' },
        { label: 'Progress', query: 'Show my progress overview' }
      ];
    } else if (path === '/spaces') {
      return [
        { label: 'Organize', query: 'Help me organize my spaces' },
        { label: 'List Spaces', query: 'What spaces do I have?' },
        { label: 'Suggestions', query: 'Suggest how to organize my content' },
        { label: 'Find Content', query: 'Help me find content in my spaces' }
      ];
    }
    
    // Default actions
    return [
      { label: 'My Content', query: 'What content do I have?' },
      { label: 'Create Trail', query: 'Create a trail' },
      { label: 'Insights', query: 'Give me insights' }
    ];
  };

  // Fetch current learning path and item for detail page actions
  const getCurrentPathItem = async () => {
    if (!params.pathId || !token) return null;
    
    try {
      const response = await fetch(`/api/learning-paths/${params.pathId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const path = data.data;
        const items = path.items || [];
        
        // Get the actual current item index from sessionStorage (what user is viewing)
        const storedIndex = sessionStorage.getItem(`learning-path-${params.pathId}-current-index`);
        const currentIndex = storedIndex !== null ? parseInt(storedIndex, 10) : 0;
        
        // Use the actual viewing index, not the first incomplete
        const currentItem = items[currentIndex] || items[0];
        
        console.log('📍 getCurrentPathItem - pathId:', params.pathId, 'currentIndex:', currentIndex, 'currentItem:', currentItem?.content?.title);
        
        return { path, currentItem, currentIndex };
      }
    } catch (error) {
      console.error('Failed to fetch path details:', error);
    }
    return null;
  };

  // Handle quick action button clicks
  const handleQuickAction = async (action: any) => {
    try {
      console.log('🎯 handleQuickAction called with:', action);
      const query = typeof action === 'string' ? action : action.query;
      const isHelpAction = typeof action === 'object' && action.isHelpAction;
      
      console.log('🎯 Quick action clicked:', query, 'isHelpAction:', isHelpAction, 'activeConversationId:', activeConversationId, 'pathId:', params.pathId, 'isOpen:', isOpen);
      
      // Ensure chat is open first
      if (!isOpen) {
        console.log('📂 Chat not open, opening...');
        openChat();
        // Wait a bit for chat to open
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Ensure a conversation exists before populating input
      if (!activeConversationId) {
        console.log('📝 No active conversation, creating one first...');
        await createNewConversation();
        // Wait for conversation to be created
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Handle learning path detail page help actions
      if (isHelpAction && params.pathId) {
        const pathItem = await getCurrentPathItem();
        if (!pathItem || !pathItem.currentItem) {
          console.error('Could not fetch current path item');
          // Fallback to regular query
          const fallbackQuery = query === 'EXPLAIN_SIMPLE' 
            ? 'Explain this content in simple terms'
            : query === 'QUIZ'
            ? 'Create a quiz about this content'
            : 'What are the key concepts in this content?';
          // Only populate input field, don't auto-send
          setCurrentMessage(fallbackQuery);
          return;
        }
        
        const { path, currentItem, currentIndex } = pathItem;
        
        // Prepare user query text
        const userQueryMap: Record<string, string> = {
          'EXPLAIN_SIMPLE': `Explain "${currentItem.content?.title || 'this content'}" in simple terms`,
          'QUIZ': `Create a quiz about "${currentItem.content?.title || 'this content'}"`,
          'KEY_CONCEPTS': `What are the key concepts in "${currentItem.content?.title || 'this content'}"?`
        };
        const userQueryText = userQueryMap[query] || `Help me understand "${currentItem.content?.title || 'this content'}"`;
        
        console.log('📝 Populating input for help action:', query, 'item:', currentItem.content?.title, 'index:', currentIndex);
        
        // Only populate input field - user will manually send
        setCurrentMessage(userQueryText);
        return;
      }
    
    // Handle "What's Next?" action
    if (query === 'WHATS_NEXT' && params.pathId) {
      const pathItem = await getCurrentPathItem();
      if (!pathItem || !pathItem.currentItem) {
        console.error('Could not fetch current path item');
        return;
      }
      
      const { path, currentItem, currentIndex } = pathItem;
      const nextItem = path.items[currentIndex + 1];
      
      const whatsNextQuery = `I'm learning about "${currentItem.content?.title || 'this content'}" (item ${currentIndex + 1} of ${path.items.length}) in my "${path.title}" learning path. ${nextItem ? `Next up is "${nextItem.content?.title}". ` : 'This is the last item. '}How does this connect to what comes next? What should I focus on before moving forward? What concepts from this item will be important for understanding the rest of the path?`;
      
      console.log('📝 Populating input for What\'s Next:', 'current item:', currentItem.content?.title, 'index:', currentIndex + 1, 'next:', nextItem?.content?.title);
      
      // Only populate input field - user will manually send
      setCurrentMessage(whatsNextQuery);
      return;
    }
    
    // Handle regular actions (Library, Learning Paths list, Spaces, etc.)
    // Only populate input field - user will manually send
    console.log('📝 Regular action - populating input field:', query);
    setCurrentMessage(query);
    
    } catch (error) {
      console.error('❌ Error in handleQuickAction:', error);
      // Fallback: try to set message anyway
      if (typeof action === 'object' && action.query) {
        setCurrentMessage(action.query);
      }
    }
  };

  // Convert markdown-like formatting to readable HTML
  const formatMessage = (content: string) => {
    let formatted = content
      // Convert **bold** to <strong>bold</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>')
      // Convert *italic* to <em>italic</em>
      .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em class="italic">$1</em>')
      // Convert `code` to styled code
      .replace(/`([^`]+)`/g, '<code class="bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
      // Convert line breaks
      .replace(/\n/g, '<br>');
    
    // Convert bullet points and numbered lists to proper HTML
    const lines = formatted.split('<br>');
    let inList = false;
    let result = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if line starts with bullet point indicators
      if (line.match(/^[•\-\*]\s+(.+)$/) || line.match(/^(\d+)\.\s+(.+)$/)) {
        if (!inList) {
          result += '<ul class="list-disc ml-6 my-2 space-y-1">';
          inList = true;
        }
        // Extract text after bullet/number
        const text = line.replace(/^[•\-\*]\s+/, '').replace(/^\d+\.\s+/, '');
        result += `<li class="text-gray-800 dark:text-gray-200">${text}</li>`;
      } else if (line.length > 0) {
        if (inList) {
          result += '</ul>';
          inList = false;
        }
        // Check if it's a header-like line (ends with :)
        if (line.endsWith(':') && line.length < 100) {
          result += `<p class="font-semibold text-gray-900 dark:text-white mt-3 mb-1">${line}</p>`;
        } else {
          result += `<p class="text-gray-800 dark:text-gray-200 mb-2">${line}</p>`;
        }
      } else {
        if (inList) {
          result += '</ul>';
          inList = false;
        }
        result += '<br class="h-2">';
      }
    }
    
    if (inList) {
      result += '</ul>';
    }
    
    return result;
  };

  const handleOpen = () => {
    console.log('🔵 handleOpen called, conversations.length:', conversations.length);
    openChat();
    resetUnread();
    
    // Only create a new conversation if we don't have an active one for this page context
    const path = location.pathname;
    let contextType = 'general';
    let conversationTitle = 'New Conversation';
    if (path === '/library') {
      contextType = 'general'; // DB stores 'general', context passed via additionalContext
      conversationTitle = 'Library Chat';
    } else if (path === '/spaces') {
      contextType = 'general'; // DB stores 'general', context passed via additionalContext
      conversationTitle = 'Spaces Chat';
    } else if (path.startsWith('/learning-paths/') && params.pathId) {
      contextType = 'learning_path'; // Learning path detail uses 'learning_path'
      conversationTitle = 'Trail Chat';
    } else if (path === '/learning-paths') {
      contextType = 'general'; // Learning paths list uses 'general'
      conversationTitle = 'Knowledge Hub Chat';
    }
    
    // Check if we already have a conversation for this context
    // For 'general' context, also match by title to find page-specific conversations
    const existingConv = conversations.find(c => {
      if (contextType === 'general') {
        return c.context_type === 'general' && 
               c.title === conversationTitle && 
               (c.messages?.length || 0) > 0;
      }
      return c.context_type === contextType && (c.messages?.length || 0) > 0;
    });
    
    if (existingConv && !activeConversationId) {
      // Reuse existing conversation
      console.log('♻️ Reusing existing conversation:', existingConv.id);
      setActiveConversation(existingConv.id);
    } else if (!activeConversationId) {
      // Create new conversation only if we don't have one
      console.log('📝 Creating fresh conversation for current page...');
      createNewConversation();
    } else {
      console.log('✅ Active conversation already exists:', activeConversationId);
    }
  };

  const handleClose = () => {
    closeChat();
  };

  const handleAction = async (action: any) => {
    switch (action.type) {
      // Content-aware actions (like in the image)
      case 'summarize_content':
        // Send a message to summarize the content
        await sendActionMessage('Please summarize this content for me');
        break;
      case 'list_key_points':
        // Send a message to list key points
        await sendActionMessage('What are the key points in this content?');
        break;
      case 'explain_simple':
        // Send a message to explain simply
        await sendActionMessage('Can you explain this like I\'m 5 years old?');
        break;
      case 'create_study_guide':
        // Send a message to create study guide
        await sendActionMessage('Create a study guide for this content');
        break;
      case 'generate_quiz':
        // Send a message to generate quiz
        await sendActionMessage('Generate a quiz based on this content');
        break;
      case 'create_learning_plan':
        // Send a message to create learning plan
        await sendActionMessage('Create a learning plan for this topic');
        break;
      
      // General learning path actions
      case 'create_learning_path':
        // Navigate to learning paths page
        window.location.href = '/learning-paths';
        break;
      case 'browse_content':
        // Navigate to library page
        window.location.href = '/library';
        break;
      case 'discover_topics':
        // Navigate to learning paths page with discover section open
        window.location.href = '/learning-paths?discover=true';
        break;
      case 'view_recommendation':
        // Navigate to library page
        window.location.href = '/library';
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const sendActionMessage = async (message: string) => {
    if (!activeConversationId || isLoading) return;

    setLoading(true);

    // Optimistically add user message
    const userMessage = {
      id: Date.now().toString(),
      role: 'user' as const,
      content: message,
      created_at: new Date().toISOString()
    };
    addMessage(activeConversationId, userMessage);

    try {
      const response = await fetch(`/api/chat/conversations/${activeConversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message })
      });

      if (response.ok) {
        const data = await response.json();
        // Add assistant message
        const assistantMessage = {
          id: data.data.messageId,
          role: 'assistant' as const,
          content: data.data.content,
          metadata: data.data.actions || {},
          created_at: new Date().toISOString()
        };
        addMessage(activeConversationId, assistantMessage);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send action message:', error);
      // Add error message
      const errorMessage = {
        id: Date.now().toString(),
        role: 'assistant' as const,
        content: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString()
      };
      addMessage(activeConversationId, errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Don't render chat on certain pages
  if (!shouldShowChat()) {
    return null;
  }

  return (
    <>
      {/* Floating Button - REMOVED per user request */}

      {/* Chat Panel - No backdrop, user can interact with page */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Chat Panel - Fabric-style: docked right, no rounded edges, no blocking backdrop */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`fixed z-50 ${
                isMaximized 
                  ? 'inset-0' 
                  : 'right-0 top-0 bottom-0'
              }`}
              style={{ 
                pointerEvents: 'none'
              }}
            >
              <div
                ref={chatPanelRef}
                data-chat-widget
                onClick={(e) => e.stopPropagation()}
                className={`flex flex-col bg-white dark:bg-gray-900 relative
                         shadow-2xl overflow-hidden border-l-2 border-gray-300 dark:border-gray-600`}
                style={{ 
                  width: isMaximized ? '100%' : '480px',
                  maxWidth: isMaximized ? '100%' : '95vw',
                  height: isMinimized ? '60px' : '100%',
                  pointerEvents: 'auto'
                }}
              >
            {/* Header - Clean minimal style */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 
                          bg-white dark:bg-gray-900 relative z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="12" width="28" height="20" rx="6" fill="#dbeafe" stroke="#1e293b" strokeWidth="2"/>
                    <rect x="10" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                    <rect x="21" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                    <path d="M14 27c1.5 1.5 6.5 1.5 8 0" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="18" y1="4" x2="18" y2="12" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="18" cy="4" r="2.5" fill="#fce7f3" stroke="#1e293b" strokeWidth="2"/>
                    <rect x="0" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                    <rect x="32" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                  </svg>
                </div>
                <span className="font-medium text-gray-900 dark:text-white text-sm">Assistant</span>
              </div>
              
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    if (isMaximized) {
                      setIsMaximized(false);
                    } else {
                      setIsMaximized(true);
                      setIsMinimized(false);
                    }
                  }}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
                  title={isMaximized ? "Exit Full Screen" : "Full Screen"}
                >
                  {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                
                <button
                  onClick={() => createNewConversation()}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
                  title="New Conversation"
                >
                  <Plus className="w-4 h-4" />
                </button>
                
                <button
                  onClick={handleClose}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 bg-white dark:bg-gray-900 relative z-10 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
                  {(() => {
                    console.log('🎨 RENDER: activeConversationId:', activeConversationId);
                    console.log('🎨 RENDER: conversations count:', conversations.length);
                    console.log('🎨 RENDER: activeConversation:', activeConversation ? {
                      id: activeConversation.id,
                      messagesCount: activeConversation.messages?.length || 0,
                      messages: activeConversation.messages
                    } : 'NULL');
                    return null;
                  })()}
                  {!activeConversation || !activeConversation.messages || activeConversation.messages.length === 0 ? (
                    <ContextualWelcome userName={user?.name} />
                  ) : (
                    <>
                      {activeConversation.messages.map((msg, idx) => (
                        <motion.div
                          key={msg.id || idx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
                        >
                          {/* Bot icon for assistant messages */}
                          {msg.role === 'assistant' && (
                            <div className="w-7 h-7 flex-shrink-0 mt-1">
                              <svg width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="4" y="12" width="28" height="20" rx="6" fill="#dbeafe" stroke="#1e293b" strokeWidth="2"/>
                                <rect x="10" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                                <rect x="21" y="18" width="5" height="5" rx="2" fill="#1e293b"/>
                                <path d="M14 27c1.5 1.5 6.5 1.5 8 0" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                                <line x1="18" y1="4" x2="18" y2="12" stroke="#1e293b" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="18" cy="4" r="2.5" fill="#fce7f3" stroke="#1e293b" strokeWidth="2"/>
                                <rect x="0" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                                <rect x="32" y="19" width="4" height="8" rx="2" fill="#93a3f8" stroke="#1e293b" strokeWidth="1.5"/>
                              </svg>
                            </div>
                          )}
                          <div
                            className={`max-w-[80%] px-4 py-3 ${
                              msg.role === 'user'
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-gray-100 rounded-2xl rounded-br-md border border-blue-100 dark:border-blue-800'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl rounded-bl-md border-2 border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <div 
                              className="text-sm whitespace-pre-wrap" 
                              dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                            />
                            
                  {/* Action Buttons - Only show on first context message */}
                  {msg.role === 'assistant' && msg.metadata && Array.isArray(msg.metadata) && msg.metadata.length > 0 && msg.id.startsWith('context-') && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.metadata.map((action: any, index: number) => (
                        <button
                          key={index}
                          onClick={() => handleAction(action)}
                          className={`px-3 py-2 text-xs rounded-lg transition-all duration-200 ${
                            action.priority === 'high'
                              ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-md hover:shadow-lg'
                              : action.priority === 'medium'
                              ? 'bg-gray-500 text-white hover:bg-gray-600 shadow-sm hover:shadow-md'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500'
                          }`}
                        >
                          <span className="font-medium">{action.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                          </div>
                        </motion.div>
                      ))}
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="flex flex-col gap-1">
                            {/* Perplexity-style thinking phases - single blue color */}
                            {thinkingPhase && location.pathname === '/library' ? (
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5">
                                  {thinkingPhase === 'searching' && (
                                    <svg className="w-3.5 h-3.5 text-blue-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                  )}
                                  {thinkingPhase === 'reading' && (
                                    <svg className="w-3.5 h-3.5 text-blue-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                  )}
                                  {thinkingPhase === 'thinking' && (
                                    <svg className="w-3.5 h-3.5 text-blue-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                  )}
                                  <span 
                                    className="text-sm font-medium text-transparent bg-clip-text"
                                    style={{
                                      backgroundImage: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 20%, #3b82f6 40%, #3b82f6 100%)',
                                      backgroundSize: '200% 100%',
                                      animation: 'shimmer 1.5s ease-in-out infinite'
                                    }}
                                  >
                                    {thinkingPhase === 'searching' && 'Searching your library...'}
                                    {thinkingPhase === 'reading' && 'Reading relevant content...'}
                                    {thinkingPhase === 'thinking' && 'Synthesizing answer...'}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <span 
                                className="text-sm font-medium text-transparent bg-clip-text"
                                style={{
                                  backgroundImage: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 20%, #3b82f6 40%, #3b82f6 100%)',
                                  backgroundSize: '200% 100%',
                                  animation: 'shimmer 1.5s ease-in-out infinite'
                                }}
                              >
                                Thinking...
                              </span>
                            )}
                          </div>
                          <style>{`
                            @keyframes shimmer {
                              0% { background-position: 200% 0; }
                              100% { background-position: -200% 0; }
                            }
                          `}</style>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Quick AI Suggestions - Above input - Only show if there are actions */}
                {getQuickActions().length > 0 && (
                <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 relative z-10">
                  <div className="flex gap-1.5 items-center flex-wrap">
                      {(() => {
                        const actions = getQuickActions();
                        const isDetailPage = params.pathId && location.pathname.startsWith('/learning-paths/') && location.pathname !== '/learning-paths';
                        return actions;
                      })().map((action) => {
                        const isDetailPage = params.pathId && location.pathname.startsWith('/learning-paths/') && location.pathname !== '/learning-paths';
                        const shouldDisable = !isDetailPage && isLoading;
                        
                        // Inline input for "Ask My Library"
                        if (action.isInlineInput) {
                          return (
                            <div key={action.label} className="flex items-center gap-0.5 flex-shrink-0">
                              <span className="px-3 py-1.5 text-xs font-medium whitespace-nowrap rounded-l-lg
                                             bg-gray-100 dark:bg-gray-700
                                             text-gray-700 dark:text-gray-200">
                                Ask my library about
                              </span>
                              <input
                                type="text"
                                value={customTopic}
                                onChange={(e) => setCustomTopic(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && customTopic.trim()) {
                                    handleTopicSelect(customTopic.trim());
                                  }
                                }}
                                placeholder="topic..."
                                className="w-20 px-2 py-1.5 text-xs
                                         bg-gray-100 dark:bg-gray-700
                                         border-y border-gray-200 dark:border-gray-600
                                         text-gray-900 dark:text-white
                                         placeholder-gray-400 dark:placeholder-gray-500
                                         focus:outline-none focus:bg-white dark:focus:bg-gray-600"
                              />
                              <button
                                onClick={() => customTopic.trim() && handleTopicSelect(customTopic.trim())}
                                disabled={!customTopic.trim() || shouldDisable}
                                className="px-2 py-1.5 rounded-r-lg
                                         bg-gray-100 dark:bg-gray-700
                                         hover:bg-gray-200 dark:hover:bg-gray-600
                                         text-gray-500 dark:text-gray-400
                                         disabled:opacity-40 disabled:cursor-not-allowed
                                         transition-colors duration-150"
                              >
                                <CornerDownLeft className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        }
                        
                        return (
                          <button
                            key={action.label}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleQuickAction(action);
                            }}
                            disabled={shouldDisable}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap flex-shrink-0
                                   bg-gray-100 dark:bg-gray-700
                                   hover:bg-blue-50 dark:hover:bg-gray-600
                                   text-gray-700 dark:text-gray-200
                                   border border-gray-200 dark:border-gray-600
                                   hover:border-blue-300 dark:hover:border-gray-500
                                   transition-all duration-150
                                   hover:scale-105 active:scale-95
                                   cursor-pointer
                                   disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                          >
                            {action.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Input */}
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 relative z-10">
                  <div className="flex items-end gap-2 bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3 border border-gray-200 dark:border-gray-700">
                    <textarea
                      ref={inputRef}
                      value={currentMessage}
                      onChange={(e) => setCurrentMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder="Ask something…"
                      disabled={isLoading}
                      className="flex-1 resize-none border-none focus:ring-0 focus:outline-none bg-transparent text-sm text-gray-900 dark:text-white placeholder:text-gray-400 max-h-40"
                      rows={2}
                      style={{ minHeight: '48px', outline: 'none' }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!currentMessage.trim() || isLoading}
                      className="p-2.5 bg-blue-500 text-white rounded-lg 
                               hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed 
                               transition-all duration-150 flex-shrink-0
                               hover:scale-105 active:scale-95
                               shadow-sm hover:shadow-md"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

